import React from 'react';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';

/**
 * Payment summary — what is being paid (GRNs / the advance), what is deducted (credit note, debit notes,
 * advance and return set-offs), the net payable, and whether the payment modes cover it.
 * The credit note is entered here; it needs the account it is credited to.
 */
const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sum = (list, key) => list.reduce((t, a) => t + (Number(a[key]) || 0), 0);

const PaymentSummarySection = ({
  grnAllocations = [],
  srnAllocations = [],
  debitNoteAllocations = [],
  advanceAllocations = [],
  advanceAmount = 0,
  paymentType = 'against_grn',
  creditNotesApplied = 0,
  onCreditNotesChange,
  // supplier credit note applied here: { accountId, label, reference } — credited to that account on approval
  creditNote = {},
  onCreditNoteChange,
  paymentModes = [],
  className = ''
}) => {
  const isAdvanceType = ['advance', 'on_account'].includes(paymentType);
  const grnTotal = sum(grnAllocations, 'amountPaid');
  const grossAmount = grnTotal + (isAdvanceType ? advanceAmount : 0);
  const creditAmount = isAdvanceType ? 0 : (Number(creditNotesApplied) || 0);
  const dnTotal = sum(debitNoteAllocations, 'amountSetoff');
  const advTotal = sum(advanceAllocations, 'amountSetoff');
  const srnTotal = sum(srnAllocations, 'amountSetoff');
  const deductions = creditAmount + dnTotal + advTotal + srnTotal;
  const net = Math.round((grossAmount - deductions) * 100) / 100;
  const modesTotal = Math.round(sum(paymentModes, 'amount') * 100) / 100;
  const difference = Math.round((net - modesTotal) * 100) / 100;

  const grossLabel = isAdvanceType ? (paymentType === 'advance' ? 'Advance amount' : 'On-account amount') : 'GRN total';

  // itemised lines for the breakdown column
  const deductionLines = [
    ...(creditAmount > 0 ? [{ key: 'cn', label: `Credit note${creditNote.reference ? ` ${creditNote.reference}` : ''}`, amount: creditAmount, tone: 'text-purple-700' }] : []),
    ...debitNoteAllocations.filter(a => a.amountSetoff > 0).map(a => ({ key: `dn-${a.debitNoteId}`, label: `Debit note ${a.debitNoteNo}`, amount: a.amountSetoff, tone: 'text-amber-700' })),
    ...advanceAllocations.filter(a => a.amountSetoff > 0).map(a => ({ key: `adv-${a.advancePayment}`, label: `${a.advanceType === 'on_account' ? 'On account' : 'Advance'} ${a.advanceNo}`, amount: a.amountSetoff, tone: 'text-teal-700' })),
    ...srnAllocations.filter(a => a.amountSetoff > 0).map(a => ({ key: `srn-${a.srnId}`, label: `Return ${a.srnNo}`, amount: a.amountSetoff, tone: 'text-orange-700' }))
  ];

  const row = (label, value, { tone = 'text-gray-900', strong = false, minus = false, testId } = {}) => (
    <div className="flex items-center justify-between py-1.5 text-sm" data-testid={testId}>
      <span className={strong ? 'font-semibold text-gray-800' : 'text-gray-600'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold' : 'font-medium'} ${tone}`}>{minus && value > 0 ? '−' : ''}{money(value)}</span>
    </div>
  );

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`} data-testid="payment-summary">
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
        <span className="text-xs font-semibold text-white">Payment Summary</span>
        <span className="text-xs text-indigo-100">Net payable <span className="font-semibold text-white tabular-nums">{money(net)}</span></span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-200">
        {/* Breakdown */}
        <div className="md:col-span-3 p-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
            {isAdvanceType ? 'Paid now' : `Paying ${grnAllocations.length} GRN${grnAllocations.length === 1 ? '' : 's'}`}
          </h4>
          {isAdvanceType ? (
            <div className="text-sm text-gray-600 py-1">
              {paymentType === 'advance' ? 'Advance' : 'On-account payment'} — set off later in an Against GRN payment.
            </div>
          ) : grnAllocations.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">Add GRNs above to see what is being paid.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {grnAllocations.map((a, i) => (
                <div key={a.grn?._id || a.grn || i} className="flex items-center justify-between py-1 text-sm">
                  <span className="text-gray-700">
                    {a.grnNo || a.grn?.grnNumber}
                    {!(a.amountPaid > 0) ? (
                      <span className="ml-2 text-[11px] text-gray-400">enter the amount to pay</span>
                    ) : a.outstandingBalance > 0 && a.amountPaid < a.outstandingBalance && (
                      <span className="ml-2 text-[11px] text-orange-600">part — {money(a.outstandingBalance - a.amountPaid)} left</span>
                    )}
                  </span>
                  <span className="tabular-nums text-gray-900">{money(a.amountPaid)}</span>
                </div>
              ))}
            </div>
          )}

          {deductionLines.length > 0 && (
            <>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-1">Deducted</h4>
              <div className="divide-y divide-gray-100">
                {deductionLines.map(d => (
                  <div key={d.key} className="flex items-center justify-between py-1 text-sm">
                    <span className={d.tone}>{d.label}</span>
                    <span className={`tabular-nums ${d.tone}`}>−{money(d.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Totals */}
        <div className="md:col-span-2 p-3 bg-gray-50/60">
          {row(grossLabel, grossAmount, { strong: true })}

          {!isAdvanceType && (
            <div className="py-1.5">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-600">Less: credit note</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={creditNotesApplied || ''}
                  onChange={(e) => onCreditNotesChange?.(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 tabular-nums"
                  aria-label="Credit note amount"
                />
              </div>
              {creditAmount > 0 && (
                <div className="mt-2 space-y-1.5 rounded border border-purple-200 bg-purple-50/60 p-2" data-testid="credit-note-details">
                  <div>
                    <label className="block text-[11px] font-medium text-purple-700 mb-0.5">Credited to account *</label>
                    <SearchableLedgerDropdown
                      value={creditNote.accountId || ''}
                      displayLabel={creditNote.label || ''}
                      onChange={(id, lbl) => onCreditNoteChange?.({ ...creditNote, accountId: id || '', label: lbl || '' })}
                      ledgerGroup={['REVENUE', 'EXPENSES']}
                      placeholder="e.g. Discount Received"
                      menuPlacement="up"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-purple-700 mb-0.5">Supplier credit note no.</label>
                    <input
                      value={creditNote.reference || ''}
                      onChange={(e) => onCreditNoteChange?.({ ...creditNote, reference: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                      placeholder="Supplier's credit note number"
                      maxLength={100}
                    />
                  </div>
                  <p className="text-[11px] text-purple-700">On approval: Dr supplier · Cr this account.</p>
                </div>
              )}
            </div>
          )}

          {dnTotal > 0 && row('Less: debit notes', dnTotal, { tone: 'text-amber-700', minus: true })}
          {advTotal > 0 && row('Less: advances set off', advTotal, { tone: 'text-teal-700', minus: true })}
          {srnTotal > 0 && row('Less: return set-offs', srnTotal, { tone: 'text-orange-700', minus: true })}

          <div className={`mt-2 flex items-center justify-between rounded-lg px-3 py-2.5 border ${net < 0 ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-100'}`}>
            <span className="text-sm font-bold uppercase tracking-wide text-indigo-900">Net payable</span>
            <span className={`text-xl font-bold tabular-nums ${net < 0 ? 'text-red-600' : 'text-indigo-700'}`} data-testid="net-payable">{money(net)}</span>
          </div>

          {/* payment modes vs net */}
          <div className="mt-2 text-xs">
            {net < 0 ? (
              <p className="text-red-600">The credit note and set-offs are {money(-net)} more than the amount being paid — reduce them.</p>
            ) : net === 0 && grossAmount > 0 ? (
              <p className="text-green-700">Fully settled by credit note / set-offs — nothing is paid out.</p>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Paid by payment modes <span className="tabular-nums text-gray-700">{money(modesTotal)}</span></span>
                {Math.abs(difference) < 0.005
                  ? <span className="font-medium text-green-700">✓ Balanced</span>
                  : <span className="font-medium text-amber-700">{difference > 0 ? `${money(difference)} still to allocate` : `${money(-difference)} too much`}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummarySection;
